import { infiniteQueryOptions, type QueryClient } from "@tanstack/react-query";
import {
  chatControllerGetMessages,
  getChatControllerGetMessagesQueryKey,
  type ChatControllerGetMessages200,
} from "@semo/api-contracts";

/** 첫 쪽(가장 최근 메시지부터)은 커서 없이 부른다. 다음 쪽부터 메시지 id 가 커서다. */
const FIRST_PAGE_CURSOR: string | undefined = undefined;

/**
 * 채팅방 메시지 목록 조회 설정 — `useChatMessages` 와 `prefetchChatMessages` 가 함께 쓴다.
 *
 * 둘이 같은 키·같은 조회 함수를 써야 미리 불러 둔 결과를 채팅창이 그대로 이어받는다
 * (어긋나면 같은 요청이 두 번 나간다).
 */
export function chatMessagesQueryOptions(roomId: string) {
  return infiniteQueryOptions({
    queryKey: [...getChatControllerGetMessagesQueryKey(roomId), "infinite"],
    queryFn: async ({ pageParam }): Promise<ChatControllerGetMessages200> => {
      return chatControllerGetMessages(roomId, {
        before: pageParam,
        limit: 50,
      });
    },
    initialPageParam: FIRST_PAGE_CURSOR,
    getNextPageParam: (lastPage) => {
      const items = lastPage.data?.items ?? [];
      if (items.length > 0) {
        return items[0]?.id;
      }
      return undefined;
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    // ✅ setQueryData 로 새 pages 를 넣어도 상위 참조가 재활용되어 리렌더가 트리거되지
    //    않는 케이스가 관찰됨(계약 서류 첨부 후 카드 상태 갱신). structural sharing 을
    //    비활성화하면 매 업데이트마다 새 참조가 보장되어 subscribers 가 안전하게 리렌더된다.
    structuralSharing: false,
  });
}

/**
 * 이 시간 안에 받아 둔 목록이면 채팅창이 붙을 때 다시 부르지 않는다.
 *
 * 발주기관 채팅창은 방 정보를 기다리는 동안 메시지를 미리 불러 둔다(`prefetchChatMessages`).
 * 방 정보가 오고 채팅창이 붙는 순간 같은 요청을 또 보내지 않게 하는 여유다. 붙은 뒤 실시간
 * 연결이 맺어지면 한 번 더 맞춰 오므로(`useChatMessages` 의 Gap Fill) 그 사이 들어온 메시지도
 * 놓치지 않는다.
 */
export const CHAT_MESSAGES_JUST_FETCHED_MS = 2_000;

/** 채팅창이 붙을 때 메시지를 다시 부를지 — 방금 받아 둔 목록이면 그대로 쓴다. */
export function chatMessagesRefetchOnMount(query: {
  state: { dataUpdatedAt: number };
}): boolean | "always" {
  const age = Date.now() - query.state.dataUpdatedAt;
  return age >= 0 && age < CHAT_MESSAGES_JUST_FETCHED_MS ? false : "always";
}

/**
 * 채팅방 메시지 목록을 미리 부른다 — 방 정보와 **동시에** 부르려고 쓴다.
 *
 * 메시지 조회에 필요한 값은 방 번호뿐이라 방 정보를 기다릴 이유가 없다. 채팅창이 방 정보를
 * 받은 뒤에야 메시지를 부르던 것(조회 둘이 차례로 걸리던 것)을 둘이 함께 나가게 한다.
 * 실패해도 던지지 않는다 — 채팅창이 붙으면서 다시 부르고, 방 입장 실패는 방 정보 쪽이 알린다.
 */
export function prefetchChatMessages(
  queryClient: QueryClient,
  roomId: string,
): Promise<void> {
  return queryClient.prefetchInfiniteQuery(chatMessagesQueryOptions(roomId));
}
